import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axiosConfig';
import { AuthContext } from './auth-provider';

export const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  markAsRead: () => {},
  markAllAsRead: () => {},
  clearAll: () => {},
});

export const useNotifications = () => useContext(NotificationContext);

const getRuntimeConfig = () => {
  if (typeof window === "undefined") {
    return {};
  }

  return window.__APP_CONFIG__ || {};
};

const resolveNotificationsWsUrl = () => {
  const runtimeConfig = getRuntimeConfig();
  const buildTimeUrl =
    typeof process !== "undefined" && process.env
      ? process.env.REACT_APP_WS_NOTIFICATIONS_URL
      : undefined;
  const configuredUrl =
    runtimeConfig.REACT_APP_WS_NOTIFICATIONS_URL ||
    buildTimeUrl;

  if (configuredUrl) {
    const baseUrl = configuredUrl.replace(/\/$/, "");
    return `${baseUrl}/ws/notifications/`;
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/ws/notifications/`;
};

export default function NotificationProvider({ children }) {
  const { isAuthenticated, grantedFeatures} = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newNotification, setNewNotification] = useState(null);
  const [user_has_rights_notifications, set_user_has_rights_notifications] = useState(null);

  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const reconnectEnabled = useRef(false);
  useEffect(() => {
    if(grantedFeatures != null){
      set_user_has_rights_notifications(grantedFeatures.includes('edp_user_notification'));
    }
  }, [grantedFeatures])

  const upsertNotification = useCallback((notification) => {
    if (!notification || notification.id == null) {
      return;
    }

    setNotifications((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === notification.id);

      if (existingIndex === 0) {
        return prev;
      }

      if (existingIndex > 0) {
        const next = [...prev];
        next.splice(existingIndex, 1);
        return [notification, ...next];
      }

      return [notification, ...prev];
    });
  }, []);

  const fetchNotifications = useCallback(() => {
    if (!isAuthenticated || !user_has_rights_notifications) return;
    
    axios.get(`${window.API_URL}/api/v1.0/notifications/`)
      .then(response => {
        setNotifications(response.data);
      })
      .catch(err => console.error("Erreur lors du chargement des notifications:", err));

    axios.get(`${window.API_URL}/api/v1.0/notifications/unread-count/`)
      .then(response => {
        setUnreadCount(response.data.unread_count);
      })
      .catch(err => console.error("Erreur lors du comptage des notifications:", err));
  }, [isAuthenticated, user_has_rights_notifications]);

  const connectWS = useCallback(() => {
    if (!isAuthenticated || !user_has_rights_notifications) return;

    const wsUrl = resolveNotificationsWsUrl();

    console.log("Connexion WebSocket notifications:", wsUrl);
    
    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      console.log("WebSocket notifications connecté.");
      fetchNotifications();
    };

    ws.current.onmessage = (event) => {
      const payload = JSON.parse(event.data);
      console.log("Nouvelle notification reçue:", payload);
      
      if (payload.type === 'notification') {
        const data = payload.data;
        // Ajouter ou déplacer en tête sans créer de doublon
        upsertNotification(data);
        setUnreadCount(prev => prev + 1);
        
        // Déclencher le toast
        setNewNotification(data);
        setTimeout(() => setNewNotification(null), 5000);
      } else if (payload.type === 'broadcast') {
        // Pour les broadcasts, on rafraîchit la liste complète pour avoir les IDs en base
        fetchNotifications();
        
        // Déclencher le toast
        setNewNotification(payload.data);
        setTimeout(() => setNewNotification(null), 5000);
      }
    };

    ws.current.onclose = (e) => {
      if (reconnectEnabled.current) {
        console.log("WebSocket notifications déconnecté. Tentative de reconnexion dans 5s...", e.reason);
        reconnectTimeout.current = setTimeout(connectWS, 5000);
      }
    };

    ws.current.onerror = (err) => {
      console.error("Erreur WebSocket notifications:", err);
      ws.current.close();
    };
  }, [isAuthenticated, fetchNotifications, upsertNotification, user_has_rights_notifications]);

  useEffect(() => {
    if (!user_has_rights_notifications) return;
    if (!isAuthenticated) {
      reconnectEnabled.current = false;
      if (ws.current) ws.current.close();
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }

    reconnectEnabled.current = true;
    fetchNotifications();
    connectWS();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchNotifications();
      }
    };

    window.addEventListener("focus", fetchNotifications);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      reconnectEnabled.current = false;
      window.removeEventListener("focus", fetchNotifications);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (ws.current) ws.current.close();
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
    };
  }, [isAuthenticated, fetchNotifications, connectWS, user_has_rights_notifications]);

  const markAsRead = (id) => {
    axios.patch(`${window.API_URL}/api/v1.0/notifications/${id}/read/`)
      .then(() => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      });
  };

  const markAllAsRead = () => {
    axios.post(`${window.API_URL}/api/v1.0/notifications/read-all/`)
      .then(() => {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
      });
  };

  const clearAll = () => {
    axios.delete(`${window.API_URL}/api/v1.0/notifications/clear-all/`)
      .then(() => {
        // On ne retire de l'interface que les notifications lues de type TASK
        // Les RELEASE (Annonces) restent visibles.
        setNotifications(prev => prev.filter(n => !n.is_read || n.notification_type === 'RELEASE'));
      });
  };

  return user_has_rights_notifications ? (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      newNotification,
      markAsRead,
      markAllAsRead,
      clearAll
    }}>
      {children}
    </NotificationContext.Provider>
  ) : (
    <>{children}</>
  );
}
